import { httpsCallable } from 'firebase/functions';
import firebaseFunctions from '~/config/firebase/functions';

export type AdminUserRow = {
  userCompanyId: string;
  uid: string;
  name: string;
  email: string;
  role: { id: string; name: string };
  locations: { id: string; name: string; address: string }[];
  disabled: boolean;
};

type CreateUserPayload = {
  name: string;
  email: string;
  password: string;
  roleId: string;
  companyId: string;
  locationIds: string[];
};

type UpdateUserPayload = {
  uid: string;
  userCompanyId: string;
  name?: string;
  email?: string;
  password?: string;
  roleId?: string;
  locationIds?: string[];
};

const callList = httpsCallable<{ companyId: string }, { users: AdminUserRow[] }>(
  firebaseFunctions,
  'adminListCompanyUsers'
);
const callCreate = httpsCallable<CreateUserPayload, { uid: string; userCompanyId: string }>(
  firebaseFunctions,
  'adminCreateUser'
);
const callUpdate = httpsCallable<UpdateUserPayload, { uid: string }>(
  firebaseFunctions,
  'adminUpdateUser'
);
const callSetDisabled = httpsCallable<
  { uid: string; disabled: boolean },
  { uid: string; disabled: boolean }
>(firebaseFunctions, 'adminSetUserDisabled');
const callRevokeSessions = httpsCallable<{ uid: string }, { uid: string }>(
  firebaseFunctions,
  'adminRevokeUserSessions'
);

/**
 * Las Cloud Functions devuelven un mensaje ya redactado para el usuario final.
 * Solo se sustituye cuando el fallo es de red o no trae mensaje.
 */
export const getReadableError = (error: unknown): string => {
  const message = (error as { message?: string })?.message;
  const code = (error as { code?: string })?.code;

  if (code === 'functions/unauthenticated') {
    return 'Su sesión expiró. Vuelva a iniciar sesión.';
  }
  if (code === 'functions/internal' && !message) {
    return 'No se pudo completar la operación. Intente de nuevo.';
  }
  if (!message || message === 'internal') {
    return 'No se pudo conectar con el servidor. Revise su conexión.';
  }
  return message;
};

export const listCompanyUsers = async (companyId: string): Promise<AdminUserRow[]> => {
  const result = await callList({ companyId });
  return result.data.users ?? [];
};

export const createUser = async (payload: CreateUserPayload) => {
  const result = await callCreate(payload);
  return result.data;
};

export const updateUser = async (payload: UpdateUserPayload) => {
  const result = await callUpdate(payload);
  return result.data;
};

export const setUserDisabled = async (uid: string, disabled: boolean) => {
  const result = await callSetDisabled({ uid, disabled });
  return result.data;
};

/** Cierra la sesión del usuario en todos sus dispositivos, al instante. */
export const revokeUserSessions = async (uid: string) => {
  const result = await callRevokeSessions({ uid });
  return result.data;
};

/** Genera una contraseña legible y suficientemente fuerte para entregar a mano. */
export const generatePassword = (length = 14): string => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const symbols = '!@#$%&*';
  const values = new Uint32Array(length);
  crypto.getRandomValues(values);

  let password = '';
  for (let i = 0; i < length - 2; i++) {
    password += alphabet[values[i] % alphabet.length];
  }
  // Garantiza al menos un dígito y un símbolo.
  password += '23456789'[values[length - 2] % 8];
  password += symbols[values[length - 1] % symbols.length];

  return password;
};
